import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
  Tooltip,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCalendar,
  IconDeviceFloppy,
  IconList,
  IconSearch,
  IconSwitch,
  IconX,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  repositoryDelivery,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import {
  changeSearchValue,
  getValueById,
} from "../../../_base/helper/FunctionHelper";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getMasterOrgSelect,
  getTblInventoryTransactionType3,
} from "../../../service/getSelectApi";
import MultiSelectCustom from "../../../common/MultiSelectCustom/MultiSelectCustom";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

// Giá trị người dùng đang gõ của 1 dòng đang ở chế độ chỉnh sửa.
// note nhập ở modal xác nhận, mỗi dòng một lý do riêng theo đúng nghiệp vụ.
type EditedRow = {
  itemCode: string;
  serialNumber: string;
  note: string;
};

// Ô nhập được phép chỉnh sửa trực tiếp trên bảng
type EditableCellField = Extract<keyof EditedRow, "itemCode" | "serialNumber">;

// Một dòng thực sự có thay đổi so với dữ liệu gốc
type RowChange = {
  transactionId: number;
  maGd: string;
  oldItemCode: string;
  newItemCode: string;
  oldSerialNumber: string;
  newSerialNumber: string;
  itemCodeChanged: boolean;
  serialChanged: boolean;
  note: string;
};

type AdjustItem = {
  transactionId: number;
  newSerialNumber: string | null;
  newItemCode: string | null;
  note: string | null;
};

// Option cho ô chọn "Mã sản phẩm" khi chỉnh sửa: kèm luôn tên sản phẩm để
// đồng bộ sang cột "Tên sản phẩm" ngay khi người dùng chọn mã mới.
type ItemCodeOption = { value: string; label: string; itemName: string };

type ItemCodeEditCellProps = {
  transactionId: number;
  initialCode: string;
  options: ItemCodeOption[];
  selected?: { itemCode: string; itemName: string };
  onFetch: (key: string) => void;
  onSelect: (transactionId: number, itemCode: string, itemName: string) => void;
};

// Ô chọn "Mã sản phẩm" ở chế độ sửa. Tách thành component riêng + React.memo để
// việc gõ tìm kiếm chỉ re-render đúng ô này, không đụng tới cả bảng.
const ItemCodeEditCell = React.memo(function ItemCodeEditCell({
  transactionId,
  initialCode,
  options,
  selected,
  onFetch,
  onSelect,
}: ItemCodeEditCellProps) {
  const [search, setSearch] = useState("");

  // Chỉ gọi API lấy danh sách khi người dùng đã nhập tối thiểu 2 ký tự.
  // Debounce 300ms để không bắn request theo từng phím bấm.
  useEffect(() => {
    const key = search.trim();
    if (key.length < 2) return;
    const timer = setTimeout(() => onFetch(key), 300);
    return () => clearTimeout(timer);
  }, [search, onFetch]);

  const currentValue = selected?.itemCode ?? initialCode ?? "";

  // Đảm bảo option đang chọn luôn có mặt trong data để Select hiển thị đúng mã,
  // kể cả khi danh sách vừa fetch chưa chứa mã đó. label = mã để ô chỉ hiện mã.
  const data = React.useMemo<ItemCodeOption[]>(() => {
    if (currentValue && !options.some((o) => o.value === currentValue)) {
      return [
        {
          value: currentValue,
          label: currentValue,
          itemName: selected?.itemName ?? "",
        },
        ...options,
      ];
    }
    return options;
  }, [options, currentValue, selected]);

  const notEnoughChars = search.trim().length < 2;

  return (
    <Select
      size="xs"
      variant="filled"
      searchable
      placeholder="Nhập ≥ 2 ký tự để tìm mã SP"
      data={data}
      value={currentValue || null}
      onSearchChange={setSearch}
      // Đã lọc phía server theo key -> giữ nguyên toàn bộ option trả về,
      // không để Mantine lọc lại theo label (chỉ là mã) làm mất kết quả.
      filter={({ options }) => options}
      // Ô chỉ hiển thị mã, nhưng dropdown hiển thị "mã - tên" cho dễ chọn.
      renderOption={({ option }) => {
        const name = data.find((o) => o.value === option.value)?.itemName;
        return (
          <Text size="xs">
            {name ? `${option.value} - ${name}` : option.value}
          </Text>
        );
      }}
      nothingFoundMessage={
        notEnoughChars ? "Nhập tối thiểu 2 ký tự" : "Không tìm thấy sản phẩm"
      }
      comboboxProps={{ withinPortal: true }}
      onChange={(val) => {
        if (!val) return;
        const opt = data.find((o) => o.value === val);
        onSelect(transactionId, val, opt?.itemName ?? "");
      }}
      styles={{ input: { fontSize: "12.5px", fontWeight: 500 } }}
    />
  );
});

const InventoryTransferWarranty = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const [isCallFirst, setIsCallFirst] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    startDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.startDate
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() - 14)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )?.toISOString(),
    endDate:
      _searchJson && checkedPinSearch
        ? _searchJson?.endDate
        : new Date(
            new Date(
              new Date(new Date().setDate(new Date().getDate() + 1)).setHours(
                0,
                0,
                0,
                0,
              ),
            ).getTime() +
              7 * 60 * 60 * 1000,
          )?.toISOString(),
    MaChiNhanhs: _searchJson && checkedPinSearch ? _searchJson.MaChiNhanhs : [],
    MaViTriList:
      _searchJson && checkedPinSearch ? _searchJson?.MaViTriList : [],
    MaVatTu: _searchJson && checkedPinSearch ? _searchJson?.MaVatTu : [],
    MaKho: _searchJson && checkedPinSearch ? _searchJson?.MaKho : [],
    LoaiGdNhapXuat:
      _searchJson && checkedPinSearch ? _searchJson?.LoaiGdNhapXuat : "",
    LoaiChungTuList:
      _searchJson && checkedPinSearch ? _searchJson?.LoaiChungTuList : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const [dataBranchSelect, setDataBranchSelect] = useState<any[]>([]);
  const [selectIdBranch, setSelectIdBranch] = useState<string[]>([]);
  const [dataInvSelect, setDataInvSelect] = useState<any[]>([]);
  const [selectIdInv, setSelectIdInv] = useState<string[]>([]);
  const [dataPositionInvSelect, setDataPositionInvSelect] = useState<any[]>([]);
  const [searchItemCode, setSearchItemCode] = useState<string>("");
  const [dataItemCode, setDataItemCode] = useState<ComboboxItem[]>([]);
  // Danh sách sản phẩm phục vụ riêng cho việc sửa "Mã sản phẩm" trên bảng
  // (tách khỏi dataItemCode của bộ lọc). Chỉ được nạp khi gõ >= 2 ký tự.
  const [dataItemCodeEdit, setDataItemCodeEdit] = useState<ItemCodeOption[]>(
    [],
  );
  // Mã sản phẩm đang được chọn cho từng dòng sửa -> dùng để đổi tên SP ở cột bên cạnh.
  const [itemCodeSelections, setItemCodeSelections] = useState<
    Map<number, { itemCode: string; itemName: string }>
  >(new Map());
  const [dataInventoryTransactionType, setDataInventoryTransactionType] =
    useState<ComboboxItem[]>([]);

  //#region Chỉnh sửa Mã sản phẩm / Serial
  // Giá trị đang gõ được giữ trong ref chứ không phải state: mỗi ký tự chỉ ghi vào Map,
  // không setState -> bảng (tối đa 100 dòng x 15 cột) không re-render theo từng phím bấm.
  const editedValuesRef = React.useRef<Map<number, EditedRow>>(new Map());
  // Chỉ state này mới gây re-render, và chỉ đổi khi vào/thoát chế độ sửa - thao tác hiếm.
  const [editingRowIds, setEditingRowIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpened, { open: openConfirmModal, close: closeConfirmModal }] =
    useDisclosure(false);
  const isEditing = editingRowIds.size > 0;

  const startEditRow = React.useCallback((row: any) => {
    const transactionId = row?.original?.transactionId;
    if (transactionId === null || transactionId === undefined) return;

    if (!editedValuesRef.current.has(transactionId)) {
      editedValuesRef.current.set(transactionId, {
        itemCode: row.original.itemCode ?? "",
        serialNumber: row.original.serialNumber ?? "",
        note: "",
      });
    }

    setEditingRowIds((prev) => {
      if (prev.has(transactionId)) return prev; // đã đang sửa -> không setState thừa
      const next = new Set(prev);
      next.add(transactionId);
      return next;
    });
  }, []);

  const exitEditMode = React.useCallback(() => {
    editedValuesRef.current.clear();
    // Trả lại đúng prev khi đang rỗng để React bỏ qua, tránh re-render vô ích.
    setEditingRowIds((prev) => (prev.size === 0 ? prev : new Set()));
    setItemCodeSelections((prev) => (prev.size === 0 ? prev : new Map()));
  }, []);

  // Ô chỉnh sửa dùng input KHÔNG kiểm soát (defaultValue + ghi thẳng vào ref):
  // React không re-render khi gõ nên không bị giật lag dù bảng nhiều dòng.
  const renderEditableCell = (
    row: any,
    field: EditableCellField,
    readOnlyNode: React.ReactNode,
  ) => {
    const transactionId = row?.original?.transactionId;

    if (transactionId === null || transactionId === undefined) {
      return readOnlyNode;
    }

    if (!editingRowIds.has(transactionId)) {
      return (
        <Tooltip label="Double click để chỉnh sửa" openDelay={500}>
          <Box onDoubleClick={() => startEditRow(row)}>{readOnlyNode}</Box>
        </Tooltip>
      );
    }

    return (
      <TextInput
        // key gắn theo dòng để khi vào lại chế độ sửa input đọc lại defaultValue mới nhất
        key={`${field}-${transactionId}`}
        size="xs"
        variant="filled"
        defaultValue={editedValuesRef.current.get(transactionId)?.[field] ?? ""}
        onChange={(e) => {
          const input = e.currentTarget;
          const upper = input.value.toUpperCase();

          // Mã sản phẩm / Serial bắt buộc viết hoa. Input không kiểm soát nên phải
          // ghi thẳng vào DOM và khôi phục vị trí con trỏ, nếu không con trỏ sẽ
          // nhảy về cuối mỗi khi sửa ở giữa chuỗi.
          if (input.value !== upper) {
            const { selectionStart, selectionEnd } = input;
            input.value = upper;
            input.setSelectionRange(selectionStart, selectionEnd);
          }

          const edited = editedValuesRef.current.get(transactionId);
          if (edited) edited[field] = upper;
        }}
        styles={{ input: { fontSize: "12.5px", fontWeight: 500 } }}
      />
    );
  };
  //#endregion

  // Dựng link sang màn chi tiết phiếu nhập kho.
  // Màn đích đọc được cả location.state lẫn query string nên mở tab mới vẫn có dữ liệu.
  const buildDetailUrl = (row: any): string | undefined => {
    const transactionCode = row?.original?.maGd;
    if (!transactionCode) return undefined;

    const params = new URLSearchParams();
    params.set("transactionCode", String(transactionCode));
    if (row?.original?.loaiGd) {
      params.set("sourceType", String(row.original.loaiGd));
    }
    params.set("isLocked", "true");

    return `/warehouse-config/detail-warehouse-in-ward?${params.toString()}`;
  };

  // Ô mã giao dịch render bằng thẻ <a> thật để Ctrl/Cmd + click hoặc click chuột giữa
  // mở được tab nền theo cơ chế native của trình duyệt.
  // Click thường không điều hướng (tránh rời trang ngoài ý muốn), double-click mở như cũ.
  const renderDetailLinkCell = (
    renderedCellValue: React.ReactNode,
    url: string | undefined,
    onNavigate: () => void,
  ) => {
    const badge = (
      <Badge
        variant="dot"
        color={renderedCellValue ? "green" : "red"}
        style={{ cursor: url ? "pointer" : "default" }}
      >
        {renderedCellValue}
      </Badge>
    );

    if (!url) return badge;

    return (
      <Tooltip label="Double click để xem chi tiết, Ctrl + click để mở tab mới">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // Có phím bổ trợ -> nhường trình duyệt xử lý, tuyệt đối không preventDefault
            // vì sẽ chặn luôn cả việc mở tab nền.
            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
            e.preventDefault();
          }}
          onDoubleClick={onNavigate}
          style={{
            color: "inherit",
            textDecoration: "none",
            display: "block",
          }}
        >
          {badge}
        </a>
      </Tooltip>
    );
  };

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "maGd",
        header: "Mã giao dịch",
        Cell: ({ renderedCellValue, row }) =>
          renderDetailLinkCell(renderedCellValue, buildDetailUrl(row), () =>
            detailDataIn(String(renderedCellValue), row.original.loaiGd, true),
          ),
        size: 150,
      },
      {
        accessorKey: "ngayGd",
        header: "Ngày giao dịch",
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
        size: 150,
      },
      {
        accessorKey: "loaiGd",
        header: "Loại giao dịch",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {getValueById(
              String(renderedCellValue),
              dataInventoryTransactionType,
              "label",
            ) || ""}
          </Text>
        ),
        size: 250,
      },
      {
        accessorKey: "maCn",
        header: "Chi nhánh",
        Cell: ({ renderedCellValue, row }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue} - {row.original.tenCn}
          </Text>
        ),
        size: 200,
      },
      {
        accessorKey: "maKho",
        header: "Kho",
        Cell: ({ renderedCellValue, row }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue} - {row.original.tenKho}
          </Text>
        ),
        size: 200,
      },
      {
        accessorKey: "maViTri",
        header: "Mã vị trí",
        size: 140,
      },
      {
        accessorKey: "itemCode",
        header: "Mã sản phẩm",
        Cell: ({ renderedCellValue, row }) => {
          const transactionId = row?.original?.transactionId;
          const readOnlyNode = <Badge color="blue">{renderedCellValue}</Badge>;

          if (transactionId === null || transactionId === undefined) {
            return readOnlyNode;
          }

          if (!editingRowIds.has(transactionId)) {
            return (
              <Tooltip label="Double click để chỉnh sửa" openDelay={500}>
                <Box onDoubleClick={() => startEditRow(row)}>
                  {readOnlyNode}
                </Box>
              </Tooltip>
            );
          }

          return (
            <ItemCodeEditCell
              transactionId={transactionId}
              initialCode={row.original.itemCode ?? ""}
              options={dataItemCodeEdit}
              selected={itemCodeSelections.get(transactionId)}
              onFetch={handleGetDataItemCodeEdit}
              onSelect={handleSelectItemCode}
            />
          );
        },
        size: 200,
      },
      {
        accessorKey: "itemName",
        header: "Tên sản phẩm",
        Cell: ({ renderedCellValue, row }) => {
          const transactionId = row?.original?.transactionId;
          const selection =
            transactionId === null || transactionId === undefined
              ? undefined
              : itemCodeSelections.get(transactionId);

          // Đang sửa và đã chọn mã mới -> hiển thị tên SP tương ứng mã vừa chọn.
          if (selection && editingRowIds.has(transactionId)) {
            return (
              <Text fw={500} size="12.5px" c="blue">
                {selection.itemName}
              </Text>
            );
          }

          return renderedCellValue;
        },
        size: 300,
      },
      {
        accessorKey: "serialNumber",
        header: "Serial",
        Cell: ({ renderedCellValue, row }) =>
          renderEditableCell(
            row,
            "serialNumber",
            <Badge color="blue" variant="light">
              {renderedCellValue}
            </Badge>,
          ),
        size: 200,
      },
      {
        accessorKey: "serialStatus",
        header: "Trạng thái serial",
      },
      {
        accessorKey: "soLuongNhap",
        header: "Số lượng nhập",
      },
      {
        accessorKey: "donGia",
        header: "Đơn giá",
      },
      {
        accessorKey: "phongBanGd",
        header: "Phòng ban giao dịch",
      },
      {
        accessorKey: "tenNv",
        header: "Tên nhân viên",
        size: 200,
      },
      {
        accessorKey: "dienGiai",
        header: "Diễn giải",
        size: 300,
      },
    ],
    [
      dataInventoryTransactionType,
      editingRowIds,
      dataItemCodeEdit,
      itemCodeSelections,
    ],
  );

  const detailDataIn = (
    id: string | number,
    type: string | number | null,
    isLocked: boolean,
  ) => {
    navigate("/warehouse-config/detail-warehouse-in-ward", {
      state: { transactionCode: id, sourceType: type, isLocked: isLocked },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/TblItemTransaction/get-warranty-supplier-import-items?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params?.toString()}`;

      const res = await repositoryDelivery.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  //#region Lưu chỉnh sửa Mã sản phẩm / Serial
  // Gom các dòng thực sự có thay đổi. Dùng chung cho modal xác nhận và payload gửi API.
  const collectChanges = (): RowChange[] => {
    const originById = new Map<number, any>(
      data.map((item) => [item.transactionId, item]),
    );

    return Array.from(editingRowIds).reduce<RowChange[]>(
      (acc, transactionId) => {
        const edited = editedValuesRef.current.get(transactionId);
        const origin = originById.get(transactionId);
        if (!edited || !origin) return acc;

        const oldItemCode = String(origin.itemCode ?? "").trim();
        const oldSerialNumber = String(origin.serialNumber ?? "").trim();
        const newItemCode = edited.itemCode.trim();
        const newSerialNumber = edited.serialNumber.trim();

        const itemCodeChanged = newItemCode !== oldItemCode;
        const serialChanged = newSerialNumber !== oldSerialNumber;
        if (!itemCodeChanged && !serialChanged) return acc;

        acc.push({
          transactionId,
          maGd: String(origin.maGd ?? ""),
          oldItemCode,
          newItemCode,
          oldSerialNumber,
          newSerialNumber,
          itemCodeChanged,
          serialChanged,
          note: edited.note.trim(),
        });
        return acc;
      },
      [],
    );
  };

  const handleOpenConfirmSave = () => {
    const changes = collectChanges();

    if (changes.length === 0) {
      NotificationExtension.Warn("Chưa có thay đổi nào để lưu !");
      return;
    }

    const invalid = changes.find(
      (item) =>
        (item.itemCodeChanged && item.newItemCode.length === 0) ||
        (item.serialChanged && item.newSerialNumber.length === 0),
    );

    if (invalid) {
      NotificationExtension.Fails(
        `Mã sản phẩm / Serial của giao dịch ${invalid.maGd} không được để trống !`,
      );
      return;
    }

    openConfirmModal();
  };

  const handleSaveEdit = async () => {
    // Field không đổi gửi null theo đúng hợp đồng API (null = giữ nguyên).
    const items: AdjustItem[] = collectChanges().map((item) => ({
      transactionId: item.transactionId,
      newSerialNumber: item.serialChanged ? item.newSerialNumber : null,
      newItemCode: item.itemCodeChanged ? item.newItemCode : null,
      note: item.note.length > 0 ? item.note : null,
    }));

    if (items.length === 0) {
      NotificationExtension.Warn("Chưa có thay đổi nào để lưu !");
      return;
    }

    setIsSaving(true);
    try {
      const res = await repositoryDelivery.post<MessageResponse<any>>(
        "/api/v1/TblItemTransaction/adjust-warranty-supplier-import",
        { items },
      );

      if (res && res.success) {
        NotificationExtension.Success("Cập nhật thành công !");
        closeConfirmModal();
        exitEditMode();
        fetchData();
      } else {
        NotificationExtension.Fails(res?.message ?? "Cập nhật thất bại !");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Cập nhật thất bại !");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = () => {
    if (isEditing) {
      NotificationExtension.Warn(
        "Vui lòng lưu hoặc hủy chỉnh sửa trước khi tìm kiếm !",
      );
      return;
    }
    fetchData();
  };

  //#endregion

  // const exportDataExcel = async () => {
  //   open();
  //   try {
  //     let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

  //     const params = new URLSearchParams();
  //     Object.entries(search).forEach(([key, value]) => {
  //       if (Array.isArray(value) && value.length > 0) {
  //         value.forEach((item) => params.append(key, String(item)));
  //       } else if (typeof value === "string" && value.length > 0) {
  //         params.append(key, value);
  //       }
  //     });

  //     if (keySearch.length > 0) {
  //       url += `&KeySearch=${keySearch?.trim()}`;
  //     }

  //     url += `&${params.toString()}`;

  //     const baseUrl = `/api/v1/ItemComparison/export-excel${url}`;

  //     const _url = BASE_API_MDM + baseUrl;
  //     const token = localStorage.getItem("token");

  //     const response = await fetch(_url, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/vnd.ms-excel",
  //         Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       NotificationExtension.Fails("Export Excel thất bại !");
  //     } else {
  //       const blob = await response.blob();
  //       const urlss = window.URL.createObjectURL(blob);
  //       const _name = new Date().toISOString().slice(0, 10);
  //       let filename = "danh_sach_voucher.xlsx";
  //       const link = document.createElement("a");
  //       link.href = urlss;
  //       link.download = filename;
  //       document.body.appendChild(link);
  //       link.click();
  //       link.remove();
  //       window.URL.revokeObjectURL(url);
  //       NotificationExtension.Success("Export Excel thành công !");
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     close();
  //   }
  // };

  const getDataBranchSelect = async () => {
    const res = await getMasterOrgSelect();

    if (res) {
      const userLoginHeader = JSON.parse(
        localStorage.getItem("userLoginHeader") || "{}",
      );

      const data = res
        ?.filter((item) => item.text && item.value)
        ?.map((item) => ({
          label: `${item.att1} - ${item.text}`,
          value: String(item.att1),
          id: String(item.value),
        }));
      const uniqueData = data?.filter(
        (item, index, arr) =>
          arr.findIndex((el) => el.value === item.value) === index,
      );
      setDataBranchSelect(uniqueData);

      if (!checkedPinSearch) {
        setSearch((prev: any) => ({
          ...prev,
          MaChiNhanhs:
            getValueById(
              String(userLoginHeader?.branchId),
              uniqueData,
              "value",
            ) &&
            String(
              getValueById(
                String(userLoginHeader?.branchId),
                uniqueData,
                "value",
              ),
            ).length > 0
              ? [
                  String(
                    getValueById(
                      String(userLoginHeader?.branchId),
                      uniqueData,
                      "value",
                    ),
                  ),
                ]
              : [],
        }));
      }

      setIsCallFirst(true);
    }
  };

  // Chỉ lấy các kho có tiền tố sau dấu "_" là BH, BK, DV (VD: K01_BH, K01_BK, K01_DV)
  const isWarrantyInventoryCode = (code?: string | null) => {
    const suffix = code?.toString()?.split("_")[1]?.toUpperCase() ?? "";
    return ["BH", "BK", "DV"].some((prefix) => suffix.startsWith(prefix));
  };

  const getDataInvSelect = async () => {
    const dataBranchSearch = selectIdBranch
      .map((item) => `&BranchId=${item}`)
      .join("");

    let url = "";

    if (selectIdBranch.length > 0) {
      url =
        `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${
          Number(selectIdBranch.length) * 20
        }` + dataBranchSearch;
    } else {
      url = `/api/v1/TblDmInventory/get-all`;
    }

    const res = await repositoryDelivery.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const result = res.data;
      if (selectIdBranch.length > 0) {
        const data = result
          ?.filter(
            (item: any) =>
              item.code && item.name && isWarrantyInventoryCode(item.code),
          )
          ?.map((item: any) => ({
            label: `${item.code} - ${item.name}`,
            value: item.code?.toString(),
            id: item.id?.toString(),
          }));
        setDataInvSelect(data);
      } else {
        const data = result
          ?.filter(
            (item: any) =>
              item.name && item.code && isWarrantyInventoryCode(item.code),
          )
          ?.map((item: any) => ({
            label: `${item.code} - ${item.name}`,
            value: item.code?.toString(),
            id: item.id?.toString(),
          }));
        setDataInvSelect(data);
      }
    }
  };

  const getDataPositionInvSelect = async () => {
    const dataInvSearch = selectIdInv
      .map((item) => `subInvIds=${item}&`)
      .join("");

    let url = "";

    if (selectIdInv.length > 0) {
      url =
        `/api/v1/TblDmItemPosition/get-select-by-subInvIds?` + dataInvSearch;
    } else {
      url = `/api/v1/TblDmItemPosition/get-all`;
    }

    const res = await repositoryDelivery.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const result = res.data;
      if (selectIdInv.length > 0) {
        const data = result
          ?.filter((item: any) => item.positionCode && item.positionName)
          ?.map((item: any) => ({
            label: `${item.positionCode} - ${item.positionName}`,
            value: item.positionCode?.toString(),
          }));
        setDataPositionInvSelect(data);
      } else {
        const data = result
          ?.filter(
            (item: any) =>
              item.positionCode && item.positionName && item.active,
          )
          ?.map((item: any) => ({
            label: `${item.positionCode} - ${item.positionName}`,
            value: item.positionCode?.toString(),
          }));
        setDataPositionInvSelect(data);
      }
    }
  };

  const handleGetDataItemCode = async () => {
    if (searchItemCode.includes(",")) return;

    let url = "/api/v1/TblItem/get-select?Page=50";

    if (searchItemCode.length > 0) {
      url += `&Key=${searchItemCode}`;
    }

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data = res.data
        .filter((i: any) => i.text && i.value)
        .map((i: any) => ({
          label: String(i.text),
          value: String(i.value),
        }));

      setDataItemCode(data);
    }
  };

  // Lấy danh sách sản phẩm cho ô sửa "Mã sản phẩm". Chỉ gọi khi key >= 2 ký tự
  // (điều kiện đã được kiểm ở ItemCodeEditCell trước khi gọi).
  const handleGetDataItemCodeEdit = React.useCallback(async (key: string) => {
    const url = `/api/v1/TblItem/get-select?Page=50&Key=${encodeURIComponent(
      key,
    )}`;

    const res = await repositoryPos.get<MessageResponse<any>>(url);

    if (res && res.success) {
      const data: ItemCodeOption[] = res.data
        .filter((i: any) => i.text && i.value)
        .map((i: any) => {
          const value = String(i.value);
          const text = String(i.text);
          const rawName =
            i.name && String(i.name).trim().length > 0
              ? String(i.name).trim()
              : "";
          // Tên SP thuần: ưu tiên trường name; nếu không có thì bỏ đúng phần mã ở
          // đầu nhãn (cắt theo độ dài mã nên không sợ mã/tên có chứa dấu "-").
          const itemName =
            rawName ||
            (text.toUpperCase().startsWith(value.toUpperCase())
              ? text
                  .slice(value.length)
                  .replace(/^[\s\-–—:.]+/, "")
                  .trim()
              : text);
          // label = mã để ô "Mã sản phẩm" chỉ hiển thị mã; tên nằm ở itemName.
          return { value, label: value, itemName };
        });

      setDataItemCodeEdit(data);
    }
  }, []);

  // Khi chọn 1 mã sản phẩm mới: ghi vào ref để lưu, đồng thời set state để
  // cột "Tên sản phẩm" bên cạnh cập nhật theo mã vừa chọn.
  const handleSelectItemCode = React.useCallback(
    (transactionId: number, itemCode: string, itemName: string) => {
      const edited = editedValuesRef.current.get(transactionId);
      if (edited) edited.itemCode = itemCode;

      setItemCodeSelections((prev) => {
        const next = new Map(prev);
        next.set(transactionId, { itemCode, itemName });
        return next;
      });
    },
    [],
  );

  const getDataInventoryTransactionType = async () => {
    try {
      const getData = await getTblInventoryTransactionType3();
      const filteredData = getData?.filter((item) => item.value && item.text);
      const mappedData = filteredData?.map((item) => ({
        value: item.value,
        label: item.text,
      }));
      const uniqueData = mappedData?.filter(
        (item, index, arr) =>
          arr.findIndex((el) => el.value === item.value) === index,
      );
      setDataInventoryTransactionType(uniqueData);
    } catch (error) {
      console.error("Error in dataTblLocation: ", error);
    }
  };

  useEffect(() => {
    if (isCallFirst) {
      fetchData();
    }
  }, [pagination?.pageIndex, pagination?.pageSize, isCallFirst]);

  // Dữ liệu vừa load lại (đổi trang / tìm kiếm) -> các dòng đang sửa không còn tương ứng,
  // thoát chế độ sửa để tránh gửi nhầm transactionId của trang cũ.
  useEffect(() => {
    exitEditMode();
  }, [data, exitEditMode]);

  useEffect(() => {
    Promise.all([getDataBranchSelect(), getDataInventoryTransactionType()]);
  }, []);

  useEffect(() => {
    getDataInvSelect();
  }, [selectIdBranch]);

  useEffect(() => {
    getDataPositionInvSelect();
  }, [selectIdInv]);

  useEffect(() => {
    handleGetDataItemCode();
  }, [searchItemCode]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningInventoryTransferWarranty",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers"],
          right: ["action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningInventoryTransferWarranty",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    // Fallback sang transactionId: nếu row.id rỗng thì mọi dòng sẽ trùng key,
    // khiến React tái sử dụng nhầm ô input không kiểm soát giữa các dòng.
    getRowId: (row) => (row.id ?? row.transactionId)?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        handleSearch();
      },
    ],
  ]);

  // Chỉ duyệt danh sách thay đổi khi modal xác nhận đang mở.
  const pendingChanges = confirmOpened ? collectChanges() : [];

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            {isEditing && (
              <>
                <Button
                  variant="default"
                  leftSection={<IconX size={16} />}
                  onClick={exitEditMode}
                >
                  Hủy chỉnh sửa
                </Button>
                <Button
                  color="green"
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleOpenConfirmSave}
                >
                  Lưu ({editingRowIds.size})
                </Button>
              </>
            )}
            {/* <Button
                leftSection={<IconFileTypeCsv size={16} />}
                onClick={() => exportDataExcel()}
              >
                Export Excel
              </Button> */}
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.startDate && search.startDate?.length > 0
                  ? new Date(search.startDate ?? "")
                  : null
              }
              maxDate={
                search.endDate && search.endDate?.length > 0
                  ? new Date(search.endDate)
                  : new Date()
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000,
                      )?.toISOString()
                    : "",
                  "startDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.endDate && search.endDate?.length > 0
                  ? new Date(search.endDate ?? "")
                  : null
              }
              minDate={
                search.startDate && search.startDate?.length > 0
                  ? new Date(search.startDate)
                  : undefined
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000,
                      )?.toISOString()
                    : "",
                  "endDate",
                  setSearch,
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelectCustom
              placeholder="Chi nhánh"
              data={dataBranchSelect}
              value={search.MaChiNhanhs ?? []}
              onChange={(val) => {
                const dataSet = dataBranchSelect
                  .filter((i) => val.includes(String(i.value)))
                  .map((i) => i.id);

                changeSearchValue(val, "MaChiNhanhs", setSearch);

                if (val && val.length > 0) {
                  setSelectIdBranch(dataSet);
                } else {
                  setSelectIdBranch([]);
                }
              }}
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy chi nhánh !"
              leftSection={<IconBuildingSkyscraper color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelectCustom
              placeholder="Kho"
              data={dataInvSelect}
              value={search.MaKho ?? []}
              onChange={(val) => {
                const dataSet = dataInvSelect
                  .filter((i) => val.includes(String(i.value)))
                  .map((i) => i.id);

                changeSearchValue(val, "MaKho", setSearch);

                if (val && val.length > 0) {
                  setSelectIdInv(dataSet);
                } else {
                  setSelectIdInv([]);
                }
              }}
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy kho !"
              leftSection={<IconBuildingWarehouse color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelectCustom
              placeholder="Vị trí kho"
              data={dataPositionInvSelect}
              value={search.MaViTriList ?? []}
              onChange={(val) => {
                changeSearchValue(val, "MaViTriList", setSearch);
              }}
              searchable
              clearable
              nothingFoundMessage="Không tìm thấy vị trí kho !"
              leftSection={<IconBuildingWarehouse color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <MultiSelectCustom
              placeholder="Mã sản phẩm"
              data={dataItemCode}
              value={search.MaVatTu ?? []}
              onChange={(val) => changeSearchValue(val, "MaVatTu", setSearch)}
              searchValue={searchItemCode}
              onSearchChange={setSearchItemCode}
              allowMultipleInput
              nothingFoundMessage="Không tìm thấy sản phẩm !"
              leftSection={<IconBarcode color="#15aabf" />}
            />
          </Grid.Col>
          {/* <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Loại chứng từ"
              leftSection={<IconSwitch color="#15aabf" />}
              data={[
                { label: "Nhập", value: "1" },
                { label: "Xuất", value: "2" },
              ]}
              clearable
              value={search.LoaiGdNhapXuat ?? ""}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "LoaiGdNhapXuat", setSearch)
              }
            />
          </Grid.Col> */}
          {/* <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Loại giao dịch"
              leftSection={<IconList color="#15aabf" />}
              data={dataInventoryTransactionType}
              searchable
              clearable
              value={search.LoaiChungTuList ?? ""}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "LoaiChungTuList", setSearch)
              }
              nothingFoundMessage="Không tìm thấy loại giao dịch !"
            />
          </Grid.Col> */}
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => handleSearch()}
              leftSection={<IconSearch size={14} />}
              disabled={isEditing}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirmModal}
        title={<Title order={5}>Xác nhận cập nhật</Title>}
        size={1000}
        centered
      >
        {/* Chỉ tính lại danh sách thay đổi khi modal mở */}
        {confirmOpened && (
          <>
            <Text size="sm" mt={8} mb="md">
              Có <b>{pendingChanges.length}</b> dòng sẽ được cập nhật, mỗi dòng
              nhập lý do chỉnh sửa riêng:
            </Text>
            <ScrollArea.Autosize mah={"55vh"} offsetScrollbars>
              {/* Viewport của ScrollArea đặt display:table cho div bọc nội dung nên margin
                  của các Paper con bị nuốt -> giãn cách bằng gap của Stack (flexbox) */}
              <Stack gap="md">
                {pendingChanges.map((item) => (
                  <Paper key={item.transactionId} withBorder radius="sm" p="md">
                    {/* Grid dùng margin âm cho gutter nên border/padding phải nằm ở Paper bọc ngoài */}
                    <Grid gutter="xl" align="flex-start">
                      <Grid.Col span={{ base: 12, md: 5 }}>
                        <Text size="xs" c="dimmed">
                          Mã giao dịch
                        </Text>
                        <Text size="sm" fw={600} mb={12}>
                          {item.maGd}
                        </Text>
                        {item.itemCodeChanged && (
                          <Flex gap={8} align="center" wrap="wrap" mb={8}>
                            <Text size="xs" c="dimmed" w={80}>
                              Mã sản phẩm
                            </Text>
                            <Text size="xs" td="line-through" c="dimmed">
                              {item.oldItemCode || "(trống)"}
                            </Text>
                            <Text size="xs">→</Text>
                            <Badge size="sm" color="blue">
                              {item.newItemCode}
                            </Badge>
                          </Flex>
                        )}
                        {item.serialChanged && (
                          <Flex gap={8} align="center" wrap="wrap">
                            <Text size="xs" c="dimmed" w={80}>
                              Serial
                            </Text>
                            <Text size="xs" td="line-through" c="dimmed">
                              {item.oldSerialNumber || "(trống)"}
                            </Text>
                            <Text size="xs">→</Text>
                            <Badge size="sm" color="blue" variant="light">
                              {item.newSerialNumber}
                            </Badge>
                          </Flex>
                        )}
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, md: 7 }}>
                        <Textarea
                          // key theo dòng để React không dùng lại DOM của dòng khác
                          key={`note-${item.transactionId}`}
                          label="Lý do chỉnh sửa"
                          placeholder="Ví dụ: Sửa do NCC gửi sai serial"
                          autosize
                          minRows={3}
                          maxRows={5}
                          // Input không kiểm soát: gõ ghi thẳng vào ref, không re-render
                          // bảng phía sau lẫn các dòng khác trong modal.
                          defaultValue={
                            editedValuesRef.current.get(item.transactionId)
                              ?.note ?? ""
                          }
                          onChange={(e) => {
                            const edited = editedValuesRef.current.get(
                              item.transactionId,
                            );
                            if (edited) edited.note = e.currentTarget.value;
                          }}
                          styles={{
                            label: { fontSize: "12px" },
                            input: { fontSize: "12.5px" },
                          }}
                        />
                      </Grid.Col>
                    </Grid>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button
                variant="default"
                onClick={closeConfirmModal}
                disabled={isSaving}
              >
                Hủy
              </Button>
              <Button
                color="green"
                leftSection={<IconDeviceFloppy size={16} />}
                loading={isSaving}
                onClick={handleSaveEdit}
              >
                Xác nhận
              </Button>
            </Flex>
          </>
        )}
      </Modal>
    </>
  );
};

export default InventoryTransferWarranty;
